import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Target } from "lucide-react";
import { PageHeader } from "@/components/layout/page-header";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { LoadingState, ErrorState } from "@/components/ui/empty";
import { useApi } from "@/lib/api";
import { formatFitness, parseJson } from "@/lib/format";
import type {
  ExperimentsResponse,
  LaddersResponse,
  Rung,
  RungsResponse,
} from "@/lib/types";

interface ThreeBaselines {
  f_random: number;
  f_trivial: number;
  f_optimal: number;
}

export function RungsPage() {
  const ladders = useApi<LaddersResponse>("/api/ladders");
  const rungs = useApi<RungsResponse>("/api/rungs");
  const experiments = useApi<ExperimentsResponse>("/api/experiments");

  const bestByRung = useMemo(() => {
    const m = new Map<string, number>();
    for (const e of experiments.data?.experiments ?? []) {
      if (e.final_fitness === null || e.final_fitness === undefined) continue;
      const prev = m.get(e.rung_id) ?? 0;
      if (e.final_fitness > prev) m.set(e.rung_id, e.final_fitness);
    }
    return m;
  }, [experiments.data]);

  const byLadderAndTier = useMemo(() => {
    const m = new Map<string, Map<number, Rung[]>>();
    for (const r of rungs.data?.rungs ?? []) {
      const ladderKey = r.ladder_id ?? "_orphan";
      const ladder = m.get(ladderKey) ?? new Map<number, Rung[]>();
      const tier = ladder.get(r.tier) ?? [];
      tier.push(r);
      ladder.set(r.tier, tier);
      m.set(ladderKey, ladder);
    }
    // Sort rungs within each tier
    for (const ladder of m.values()) {
      for (const list of ladder.values()) {
        list.sort((a, b) => a.id.localeCompare(b.id));
      }
    }
    return m;
  }, [rungs.data]);

  if (ladders.loading || rungs.loading) return <LoadingState />;
  if (ladders.error) return <ErrorState error={ladders.error} />;
  if (rungs.error) return <ErrorState error={rungs.error} />;

  const ladderList = ladders.data?.ladders ?? [];

  return (
    <>
      <PageHeader
        title="Ladders & Rungs"
        description="The validation spec. Each rung is a task the substrate must pass."
      />

      <div className="space-y-8">
        {ladderList.map((ladder) => {
          const tiersMap =
      byLadderAndTier.get(ladder.id) ?? new Map<number, Rung[]>();
          const tiers = Array.from(tiersMap.keys()).sort((a, b) => a - b);
          return (
            <section key={ladder.id}>
              <Card>
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground">
                        <Target className="h-3 w-3" />
                        <span>Ladder</span>
                        <span>·</span>
                        <span className="font-mono">v{ladder.spec_version}</span>
                      </div>
                      <CardTitle className="mt-1 text-lg">
                        {ladder.name}
                      </CardTitle>
                      <CardDescription className="mt-1 max-w-3xl text-xs">
                        {ladder.description}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {tiers.map((tier) => {
                    const tierRungs = tiersMap.get(tier) ?? [];
                    return (
                      <div key={tier}>
                        <div className="mb-2 flex items-center gap-2">
                          <div className="flex h-6 items-center rounded-full bg-muted px-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Tier {tier}
                          </div>
                          <div className="h-px flex-1 bg-border" />
                          <span className="text-[10px] text-muted-foreground">
                            {tierRungs.length} rung
                            {tierRungs.length === 1 ? "" : "s"}
                          </span>
                        </div>
                        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                          {tierRungs.map((r) => (
                            <RungCard
                              key={r.id}
                              rung={r}
                              best={bestByRung.get(r.id) ?? null}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            </section>
          );
        })}
      </div>
    </>
  );
}

function RungCard({ rung, best }: { rung: Rung; best: number | null }) {
  const baselines = parseJson<ThreeBaselines>(rung.three_baselines, {
    f_random: 0,
    f_trivial: 0,
    f_optimal: 1,
  });
  const passed = best !== null && best >= rung.fitness_threshold;
  const frac = best ?? 0;

  return (
    <div
      className={`rounded-lg border p-3 text-xs transition-colors ${
        passed
          ? "border-emerald-500/30 bg-emerald-500/5"
          : "hover:border-muted-foreground/30"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm font-semibold">{rung.id}</span>
            <Badge variant="outline" className="text-[10px]">
              predicts L{rung.layer_predicted}
            </Badge>
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {rung.name}
          </div>
        </div>
        {passed && (
          <Badge variant="success" className="shrink-0">
            PASS
          </Badge>
        )}
      </div>

      <div className="mt-3 space-y-1">
        <div className="flex items-center justify-between text-[10px]">
          <span className="text-muted-foreground">Best</span>
          <span className="font-mono tabular-nums">
            <span
              className={
                passed
                  ? "text-emerald-300"
                  : best !== null
                    ? "text-amber-300"
                    : "text-muted-foreground"
              }
            >
              {formatFitness(best)}
            </span>
            <span className="text-muted-foreground">
              {" "}
              / {rung.fitness_threshold.toFixed(2)}
            </span>
          </span>
        </div>
        <div className="relative">
          <Progress
            value={frac}
            barClassName={
              passed
                ? "bg-emerald-500"
                : best !== null
                  ? "bg-amber-400"
                  : "bg-muted-foreground/30"
            }
          />
          {/* Threshold marker */}
          <div
            className="pointer-events-none absolute top-0 h-1.5 w-px bg-foreground/70"
            style={{ left: `${rung.fitness_threshold * 100}%` }}
          />
          {/* Baseline markers */}
          <div
            className="pointer-events-none absolute top-0 h-1.5 w-px bg-zinc-500/50"
            style={{ left: `${baselines.f_random * 100}%` }}
            title={`random ${baselines.f_random}`}
          />
          <div
            className="pointer-events-none absolute top-0 h-1.5 w-px bg-sky-500/60"
            style={{ left: `${baselines.f_trivial * 100}%` }}
            title={`trivial ${baselines.f_trivial}`}
          />
        </div>
        <div className="flex justify-between text-[9px] text-muted-foreground">
          <span>random {baselines.f_random.toFixed(2)}</span>
          <span>trivial {baselines.f_trivial.toFixed(2)}</span>
          <span>optimal {baselines.f_optimal.toFixed(2)}</span>
        </div>
      </div>

      <Link
        to={`/experiments?rung=${rung.id}`}
        className="mt-3 block text-[10px] text-muted-foreground hover:text-primary"
      >
        View experiments →
      </Link>
    </div>
  );
}
